import React, {useState, useMemo} from 'react'
import {useParams, useNavigate, Link} from 'react-router-dom'
import {useQuery, useMutation, useQueryClient} from '@tanstack/react-query'
import {toast} from 'react-toastify'
import dayjs from 'dayjs'
import {KTCard, KTCardBody} from '../../../../_metronic/helpers'
import {getDocById, getDocComments, deleteDocComment, updateDocCommentStatus, toggleDocPublish, toggleDocFeatured,} from './core/_requests'
import {DeleteModal} from '../../../../_component/table/DeleteModal'
import {Skeleton} from '../../../../_component/Skeleton'
import {Pencil, Eye, EyeOff, Trash2, Search, MessageSquare, Filter, Mail, ChevronDown, Star, ChevronLeft, Clock, Tag, BookOpen,
} from 'lucide-react'

const DocDetails = () => {
  const {id} = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [deleteId, setDeleteId] = useState<string | number | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'PUBLISHED' | 'DRAFT'>('ALL')

  // Fetch document details
  const {data: doc, isLoading: isDocLoading, isError: isDocError} = useQuery(
    ['doc', id],
    () => getDocById(id),
    {enabled: !!id}
  )

  // Fetch comments specific to this document
  const {data: comments, isLoading: isCommentsLoading} = useQuery(
    ['doc-comments', id],
    () => getDocComments(id),
    {enabled: !!id}
  )

  // Toggle Publish/Draft status of the document itself
  const docPublishMutation = useMutation(
    ({docId, status}: {docId: string | number; status: 'PUBLISHED' | 'DRAFT'}) =>
      toggleDocPublish(docId, status),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['doc', id])
        queryClient.invalidateQueries(['docs'])
        toast.success('Document status updated')
      },
      onError: (error: any) => {
        const message = error.response?.data?.message || 'Failed to update document status'
        toast.error(message)
      },
    }
  )

  // Toggle Featured status of the document itself
  const docFeaturedMutation = useMutation(
    ({docId, featured}: {docId: string | number; featured: boolean}) =>
      toggleDocFeatured(docId, featured),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['doc', id])
        queryClient.invalidateQueries(['docs'])
        toast.success('Document featured status updated')
      },
      onError: (error: any) => {
        const message = error.response?.data?.message || 'Failed to update featured status'
        toast.error(message)
      },
    }
  )

  // Delete comment mutation
  const commentDeleteMutation = useMutation(deleteDocComment, {
    onSuccess: () => {
      queryClient.invalidateQueries(['doc-comments', id])
      setDeleteId(null)
      toast.error('Comment deleted successfully')
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to delete comment'
      toast.error(message)
    },
  })

  // Toggle comment status (Publish/Draft) mutation
  const commentToggleMutation = useMutation(
    ({commentId, status}: {commentId: string | number; status: 'PUBLISHED' | 'DRAFT'}) =>
      updateDocCommentStatus(commentId, status),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['doc-comments', id])
        toast.success('Comment status updated')
      },
      onError: (error: any) => {
        const message = error.response?.data?.message || 'Failed to update comment status'
        toast.error(message)
      },
    }
  )

  // Filter comments
  const filteredComments = useMemo(() => {
    if (!Array.isArray(comments)) return []

    return comments.filter((comment: any) => {
      const commentDocId = comment.doc_id ?? comment.DocId ?? comment.doc?.id
      const matchesDoc = !id || commentDocId === undefined || commentDocId === null || String(commentDocId) === String(id)

      const name = comment.name || ''
      const email = comment.email || ''
      const commentText = comment.comment || ''

      const matchesSearch =
        name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        commentText.toLowerCase().includes(searchTerm.toLowerCase())

      const matchesStatus = statusFilter === 'ALL' || comment.status === statusFilter

      return matchesDoc && matchesSearch && matchesStatus
    })
  }, [comments, id, searchTerm, statusFilter])

  if (isDocLoading) {
    return (
      <div className='flex flex-col gap-6'>
        <div className='bg-white p-6 rounded-lg border border-gray-200 shadow-sm'>
          <Skeleton width={120} height={20} className='mb-4' />
          <Skeleton width='40%' height={32} className='mb-2' />
          <Skeleton width='80%' height={16} className='mb-6' />
          <Skeleton width='100%' height={150} />
        </div>
      </div>
    )
  }

  if (isDocError || !doc) {
    return (
      <div className='bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-lg my-5'>
        <h3 className='text-lg font-bold'>Error loading document</h3>
        <p className='text-sm mt-1'>Please verify the URL or ensure the document exists.</p>
        <button
          onClick={() => navigate('/docs')}
          className='mt-3 btn btn-sm bg-red-600 text-white font-bold hover:bg-red-700'
        >
          Back to Docs
        </button>
      </div>
    )
  }

  return (
    <div className='flex flex-col gap-8 pb-10'>
      {/* Upper Navigation Header */}
      <div className='flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white rounded-lg border border-gray-200 px-6 py-4 shadow-sm'>
        <div className='flex items-center gap-3'>
          <button
            onClick={() => navigate('/docs')}
            className='inline-flex items-center justify-center w-9 h-9 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors duration-200'
            title='Back to list'
          >
            <ChevronLeft className='w-5 h-5' />
          </button>
          <div>
            <h1 className='text-xl font-bold text-gray-800 tracking-tight'>Document Details</h1>
            <p className='text-xs text-gray-500 font-medium mt-0.5'>
              View details and moderate comments on this document
            </p>
          </div>
        </div>

        <div className='flex items-center gap-3 self-end sm:self-auto'>
          {/* Feature toggle button */}
          <button
            className={`inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg border transition-all duration-200 ${
              doc.featured
                ? 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100'
                : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
            }`}
            onClick={() =>
              docFeaturedMutation.mutate({
                docId: doc.id as string | number,
                featured: !doc.featured,
              })
            }
          >
            <Star className={`w-4 h-4 ${doc.featured ? 'fill-amber-500 text-amber-500' : ''}`} />
            {doc.featured ? 'Featured' : 'Feature Doc'}
          </button>

          {/* Toggle Publish button */}
          <button
            onClick={() =>
              docPublishMutation.mutate({
                docId: doc.id as string | number,
                status: doc.status,
              })
            }
            className={`inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg transition-all duration-200 border ${
              doc.status === 'PUBLISHED'
                ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
                : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100'
            }`}
          >
            {doc.status === 'PUBLISHED' ? (
              <>
                <EyeOff className='w-4 h-4 text-emerald-600' />
                Revert to Draft
              </>
            ) : (
              <>
                <Eye className='w-4 h-4 text-gray-600' />
                Publish Document
              </>
            )}
          </button>

          {/* Edit button */}
          <Link
            to={`/docs/${doc.id}/edit`}
            className='inline-flex items-center justify-center gap-2 px-4 py-2 bg-[#008080] hover:bg-[#007373] text-white text-sm font-semibold rounded-lg shadow-sm transition-colors duration-200'
          >
            <Pencil className='w-4 h-4' />
            Edit Document
          </Link>
        </div>
      </div>

      {/* Main Grid: Left is Document Content, Right/Bottom is comments */}
      <div className='grid grid-cols-1 xl:grid-cols-3 gap-8'>
        {/* Document Info Column */}
        <div className='xl:col-span-2 flex flex-col gap-8'>
          <KTCard className='border border-gray-200 shadow-sm'>
            <KTCardBody className='p-8'>
              <div className='flex flex-wrap items-center gap-2.5 mb-5'>
                <span
                  className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold uppercase tracking-wide ${
                    doc.status === 'PUBLISHED'
                      ? 'bg-emerald-100 text-emerald-800'
                      : 'bg-amber-100 text-amber-800'
                  }`}
                >
                  {doc.status}
                </span>

                {doc.featured && (
                  <span className='inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold uppercase tracking-wide bg-amber-500 text-white'>
                    <Star className='w-3 h-3 fill-current' /> Featured
                  </span>
                )}

                <div className='text-xs text-gray-400 font-semibold flex items-center gap-1.5 ml-auto'>
                  <Clock size={13} />
                  Created {dayjs(doc.created_at).format('MMMM DD, YYYY')}
                </div>
              </div>

              <h2 className='text-3xl font-extrabold text-gray-900 leading-snug mb-4'>
                {doc.title}
              </h2>

              <div className='flex flex-wrap gap-2 mb-6'>
                <span className='text-xs text-gray-400 font-bold uppercase tracking-wider flex items-center gap-1 mr-1 mt-1'>
                  <Tag size={13} /> Categories:
                </span>
                {(doc.categories || []).map((cat) => (
                  <span
                    key={cat.id}
                    className='px-3 py-1 rounded-lg text-xs font-semibold bg-[#e6f2f2] text-[#008080] border border-[#b3d9d9]/30'
                  >
                    {cat.name}
                  </span>
                ))}
                {(doc.categories || []).length === 0 && (
                  <span className='text-gray-400 text-xs italic mt-1'>Uncategorized</span>
                )}
              </div>

              {doc.shortDesc && (
                <div className='bg-slate-50 border-l-4 border-[#008080] p-4 rounded-r-xl mb-6 text-gray-600 italic text-sm leading-relaxed'>
                  {doc.shortDesc}
                </div>
              )}

              <hr className='border-gray-100 my-6' />

              <div>
                <h3 className='text-xs font-bold text-gray-400 uppercase tracking-widest mb-3 flex items-center gap-1.5'>
                  <BookOpen size={14} /> Full Description / Content
                </h3>
                <div
                  className='prose max-w-none text-gray-800 leading-relaxed text-sm bg-gray-50/50 p-6 rounded-xl border border-gray-100/80 min-h-[150px]'
                  dangerouslySetInnerHTML={{__html: doc.description}}
                />
              </div>
            </KTCardBody>
          </KTCard>
        </div>

        {/* Comments Column */}
        <div className='xl:col-span-1 flex flex-col gap-6'>
          <div className='bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden flex flex-col min-h-[500px]'>
            {/* Header */}
            <div className='p-5 border-b border-gray-200 bg-gray-50 flex flex-col gap-3'>
              <div className='flex items-center justify-between'>
                <h3 className='text-sm font-bold text-gray-800 uppercase tracking-wider flex items-center gap-2'>
                  <MessageSquare className='w-4.5 h-4.5 text-[#008080]' />
                  Comments ({filteredComments.length})
                </h3>
              </div>

              {/* Filters */}
              <div className='grid grid-cols-1 gap-2'>
                <div className='relative'>
                  <Search className='absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400' />
                  <input
                    type='text'
                    placeholder='Search comments...'
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className='w-full pl-9 pr-4 py-2 text-xs bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#008080]/20 focus:border-[#008080] transition-all'
                  />
                </div>
                <div className='relative'>
                  <Filter className='absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400' />
                  <select
                    className='w-full pl-9 pr-4 py-1.5 bg-white border border-gray-300 rounded-lg text-[11px] font-bold text-gray-600 appearance-none focus:outline-none focus:ring-2 focus:ring-[#008080]/20 focus:border-[#008080] shadow-sm cursor-pointer'
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value as any)}
                  >
                    <option value='ALL'>All Statuses</option>
                    <option value='PUBLISHED'>Published Only</option>
                    <option value='DRAFT'>Draft Only</option>
                  </select>
                  <div className='pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-400'>
                    <ChevronDown size={14} />
                  </div>
                </div>
              </div>
            </div>

            {/* List */}
            <div className='flex-1 overflow-y-auto divide-y divide-gray-100 max-h-[600px]'>
              {isCommentsLoading ? (
                <div className='p-6 space-y-4'>
                  {[1, 2, 3].map((i) => (
                    <div key={i} className='space-y-2'>
                      <Skeleton width='40%' height={16} />
                      <Skeleton width='80%' height={12} />
                      <Skeleton width='100%' height={30} />
                    </div>
                  ))}
                </div>
              ) : filteredComments.length === 0 ? (
                <div className='p-12 text-center text-gray-400 flex flex-col items-center justify-center h-full'>
                  <MessageSquare className='w-10 h-10 mb-3 opacity-20 text-gray-500' />
                  <p className='text-xs font-bold text-gray-500'>No comments found</p>
                  <p className='text-[10px] text-gray-400 mt-1 max-w-[200px]'>
                    {searchTerm || statusFilter !== 'ALL'
                      ? 'Try clearing the search or status filter options.'
                      : 'Comments submitted for this documentation will appear here.'}
                  </p>
                </div>
              ) : (
                filteredComments.map((comment) => (
                  <div key={comment.id} className='p-5 hover:bg-gray-50/50 transition-colors duration-150 flex flex-col gap-3'>
                    <div className='flex items-start gap-3'>
                      <div className='w-8 h-8 rounded-full bg-teal-50 border border-teal-100 flex items-center justify-center flex-shrink-0 text-xs font-extrabold text-[#008080]'>
                        {comment.name?.charAt(0).toUpperCase() || '?'}
                      </div>
                      <div className='flex-1 min-w-0'>
                        <h4 className='text-sm font-bold text-gray-900 truncate'>{comment.name}</h4>
                        <div className='flex items-center gap-1.5 text-[10px] text-gray-400 mt-0.5'>
                          <Mail size={11} className='flex-shrink-0' />
                          <span className='truncate'>{comment.email}</span>
                        </div>
                      </div>
                      <span className='text-[9px] text-gray-400 font-semibold whitespace-nowrap'>
                        {dayjs(comment.created_at).format('MMM DD, YYYY')}
                      </span>
                    </div>

                    <div className='bg-gray-50 border border-gray-100 rounded-lg p-3 text-xs text-gray-700 leading-relaxed font-medium whitespace-pre-wrap'>
                      {comment.comment}
                    </div>

                    <div className='flex items-center justify-between mt-1'>
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider ${
                          comment.status === 'PUBLISHED'
                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-100'
                            : 'bg-amber-50 text-amber-700 border border-amber-100'
                        }`}
                      >
                        {comment.status === 'PUBLISHED' ? 'Published' : 'Draft'}
                      </span>

                      <div className='flex items-center gap-1.5'>
                        {/* Publish toggle */}
                        <button
                          onClick={() =>
                            commentToggleMutation.mutate({
                              commentId: comment.id as string | number,
                              status: comment.status === 'PUBLISHED' ? 'DRAFT' : 'PUBLISHED',
                            })
                          }
                          className={`inline-flex items-center justify-center w-7 h-7 rounded-md transition-colors duration-200 border ${
                            comment.status === 'PUBLISHED'
                              ? 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'
                              : 'bg-emerald-50 text-emerald-600 border-emerald-100 hover:bg-emerald-100'
                          }`}
                          title={comment.status === 'PUBLISHED' ? 'Draft comment' : 'Publish comment'}
                        >
                          {comment.status === 'PUBLISHED' ? (
                            <EyeOff className='w-3.5 h-3.5' />
                          ) : (
                            <Eye className='w-3.5 h-3.5' />
                          )}
                        </button>

                        {/* Delete comment */}
                        <button
                          onClick={() => setDeleteId(comment.id as string | number)}
                          className='inline-flex items-center justify-center w-7 h-7 rounded-md bg-rose-50 text-rose-600 border border-rose-100 hover:bg-rose-100 transition-colors duration-200'
                          title='Delete comment'
                        >
                          <Trash2 className='w-3.5 h-3.5' />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Delete confirmation modal */}
      <DeleteModal
        showDeleteConfirm={deleteId !== null}
        setShowDeleteConfirm={(show) => {
          if (!show) setDeleteId(null)
        }}
        deleteAction={(cid) => commentDeleteMutation.mutateAsync(cid)}
        refetch={() => queryClient.invalidateQueries(['doc-comments', id])}
        id={deleteId}
      />
    </div>
  )
}

export default DocDetails
